/**
 * Platform Billing Service - STUB VERSION
 *
 * Temporarily disabled due to expo-in-app-purchases incompatibility with Expo SDK 54
 * This stub allows the app to build while we work on subscription configuration
 *
 * IMPORTANT: This service must be used instead of Stripe for mobile app subscriptions
 * to comply with Google Play and Apple App Store policies.
 *
 * TODO: Re-enable with compatible IAP package after subscription products are configured
 */

import { Platform } from 'react-native';

import {
  getPlatformProducts,
  mapProductToPlan,
  PlatformProduct
} from '../config/platformProducts';

export interface PurchaseResult {
  success: boolean;
  purchaseToken?: string;
  transactionId?: string;
  productId: string;
  planId: string;
  billingInterval: 'monthly' | 'annual';
  platform: 'ios' | 'android';
  error?: string;
}

export interface ProductDetails {
  productId: string;
  title: string;
  description: string;
  price: string;
  priceAmountMicros: string;
  priceCurrencyCode: string;
  type: 'subscription' | 'consumable';
}

class PlatformBillingService {
  private isConnected: boolean = false;

  /**
   * Initialize and connect to platform billing - STUB
   */
  async initialize(): Promise<boolean> {
    console.warn('Platform billing is currently disabled - stub version');
    return false;
  }

  /**
   * Disconnect from platform billing - STUB
   */
  async disconnect(): Promise<void> {
    console.warn('Platform billing is currently disabled - stub version');
  }

  /**
   * Get available products - STUB
   */
  async getProducts(): Promise<ProductDetails[]> {
    console.warn('Platform billing is currently disabled - stub version');
    return [];
  }

  /**
   * Purchase a subscription product - STUB
   */
  async purchaseProduct(productId: string): Promise<PurchaseResult> {
    console.warn('Platform billing is currently disabled - stub version');
    const planMapping = mapProductToPlan(productId);
    return {
      success: false,
      productId,
      planId: planMapping?.planId || '',
      billingInterval: planMapping?.billingInterval || 'monthly',
      platform: Platform.OS === 'ios' ? 'ios' : 'android',
      error: 'Platform billing temporarily disabled'
    };
  }

  /**
   * Get purchase history - STUB
   */
  async getPurchaseHistory(): Promise<any[]> {
    console.warn('Platform billing is currently disabled - stub version');
    return [];
  }

  /**
   * Restore previous purchases - STUB
   */
  async restorePurchases(): Promise<any[]> {
    console.warn('Platform billing is currently disabled - stub version');
    return [];
  }

  /**
   * Finish a transaction - STUB
   */
  async finishTransaction(purchase: any): Promise<boolean> {
    console.warn('Platform billing is currently disabled - stub version');
    return false;
  }

  /**
   * Get platform-specific subscription management URL
   */
  getSubscriptionManagementUrl(): string {
    if (Platform.OS === 'ios') {
      return 'https://apps.apple.com/account/subscriptions';
    } else {
      return 'https://play.google.com/store/account/subscriptions';
    }
  }

  /**
   * Check if platform billing is available - STUB
   */
  async isPlatformBillingAvailable(): Promise<boolean> {
    return false; // Temporarily disabled
  }

  /**
   * Get product configuration for current platform
   */
  getProductConfiguration(): PlatformProduct[] {
    const platform = Platform.OS === 'ios' ? 'ios' : 'android';
    return getPlatformProducts(platform);
  }
}

// Export singleton instance
export const platformBillingService = new PlatformBillingService();

// Export types
export type { PlatformProduct };
